import { Card, Col, DatePicker, Form, Input, Radio, Row } from 'antd'
import { FormInstance } from 'antd/lib'
import { useEffect } from 'react'

import { useClassifierServiceGetCommonClassifierElementListEndpoint } from 'src/api/queries'
import { ClassifierCategory, ClassifierType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { OpportunisticDiseaseInfiniteSelect } from 'src/features/persons/components/PersonConsultations/OpportunisticDiseaseInfiniteSelect'
import { useClassifierId } from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'

import { useDiseaseOutcomeOptions } from '../useDiseaseOutcomeOptions'

type Props = {
  form: FormInstance
}

export const EpidemiologyFinalDiagnosticCard = ({ form }: Props) => {
  const { t, currentLang } = useLocales()

  const diseaseProgressId = useClassifierId(
    ClassifierType.DISEASE_PROGRESS,
    ClassifierCategory.TREATMENT
  )
  const { data: diseaseProgressOptions } =
    useClassifierServiceGetCommonClassifierElementListEndpoint(
      { id: String(diseaseProgressId) },
      [diseaseProgressId],
      { enabled: !!diseaseProgressId }
    )

  const {
    options: diseaseOutcomeOptions,
    activeOptions: activeOutcomeOptions,
    isDeath: isDeathOutcome,
    isLoaded: isOutcomeLoaded
  } = useDiseaseOutcomeOptions()

  const diseaseOutcome = Form.useWatch(
    ['form', 'finalDiagnostic', 'diseaseOutcome'],
    form
  )
  const isDeath = isDeathOutcome(diseaseOutcome?.id)

  // A stored retired outcome (e.g. "Însănătoșire", "Sechele") is not offered
  // any more, so it is cleared and must be picked again; the date only exists
  // for death.
  useEffect(() => {
    if (!isOutcomeLoaded || !diseaseOutcome?.id) return

    const isActive = activeOutcomeOptions?.some(
      (item) => item.id === diseaseOutcome.id
    )
    if (!isActive) {
      form.setFieldValue(
        ['form', 'finalDiagnostic', 'diseaseOutcome'],
        undefined
      )
    }
  }, [isOutcomeLoaded, diseaseOutcome?.id])

  useEffect(() => {
    if (isOutcomeLoaded && !isDeath) {
      form.setFieldValue(
        ['form', 'finalDiagnostic', 'diseaseOutcomeDate'],
        undefined
      )
    }
  }, [isDeath, isOutcomeLoaded])

  return (
    <Card title={t('epidemiology.final_diagnostic')}>
      <Row gutter={[24, 24]}>
        <Col span={12}>
          <Form.Item
            name={['form', 'finalDiagnostic', 'institution', 'title']}
            label={t('epidemiology.final_diagnostic_institution')}
            rules={requiredRules}
          >
            <Input
              placeholder={t('epidemiology.final_diagnostic_institution')}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['form', 'finalDiagnostic', 'institution', 'code']}
            label={t('organizational_structure.institution_code')}
            rules={requiredRules}
          >
            <Input
              placeholder={t('organizational_structure.institution_code')}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name={['form', 'finalDiagnostic', 'diagnoses']}
            label={t('epidemiology.diagnoses_title')}
            rules={requiredRules}
          >
            <OpportunisticDiseaseInfiniteSelect
              fieldName={['form', 'finalDiagnostic', 'diagnoses']}
              form={form}
              valueShape="full"
              placeholder={t('common.select_from_list')}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['form', 'finalDiagnostic', 'diagnosisDate']}
            label={t('epidemiology.final_diagnostic_date')}
            rules={requiredRules}
          >
            <DatePicker
              className="full-width"
              showTime
              showSecond={false}
              needConfirm={false}
              format={dayMonthYearHourMinutesFormatWithDot}
              disabledDate={disabledFutureDate}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['form', 'finalDiagnostic', 'diseaseProgress']}
            label={t('epidemiology.disease_progress')}
            rules={requiredRules}
            getValueProps={(val) => ({ value: val?.id })}
            getValueFromEvent={(e) => {
              const id = e?.target?.value
              if (!id) return undefined
              const found = diseaseProgressOptions?.data?.find(
                (o) => o.id === id
              )

              return found ? { id: found.id, name: found.translation } : { id }
            }}
          >
            <Radio.Group>
              {diseaseProgressOptions?.data?.map((item) => (
                <Radio key={item.id} value={item.id}>
                  {item.translation?.[currentLang.value]}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['form', 'finalDiagnostic', 'diseaseOutcome']}
            label={t('epidemiology.disease_outcome')}
            rules={requiredRules}
            getValueProps={(val) => ({ value: val?.id })}
            getValueFromEvent={(e) => {
              const id = e?.target?.value
              if (!id) return undefined
              const found = diseaseOutcomeOptions?.find((o) => o.id === id)

              return found ? { id: found.id, name: found.translation } : { id }
            }}
          >
            <Radio.Group>
              {activeOutcomeOptions?.map((item) => (
                <Radio key={item.id} value={item.id}>
                  {item?.translation?.[currentLang.value]}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>

        {isDeath && (
          <Col span={12}>
            <Form.Item
              name={['form', 'finalDiagnostic', 'diseaseOutcomeDate']}
              label={t('epidemiology.death_date')}
              rules={requiredRules}
            >
              <DatePicker
                className="full-width"
                format={dayMonthYearFormatWithDot}
                disabledDate={disabledFutureDate}
                placeholder={t('epidemiology.death_date')}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Card>
  )
}
